import React, { createRef, useState } from 'react';
import Modal from '../Modal/Modal';
import styles from './NativeShareModal.scss';
import JgButtonGroup from '@justgiving/button-group';
import { useBasePageQuery } from '../../hooks';
import { filterByTypename } from '../../utils/graphql';
import { SHARE_TYPE, NativeShare } from '../NativeShare/NativeShare';
import { useTranslation } from 'react-i18next';
import { NativeShareScreenshot } from '../NativeShareScreenshot/NativeShareScreenshot';
import { useShareContext } from '../../context/ShareContext';
import { Option } from '@justgiving/button-group/es/src/ButtonGroup';
import { NativeShareButton } from '../NativeShareButton/NativeShareButton';
import { ShareModalPlatforms } from './NativeShareEnums';

export interface NativeShareModalProps {
  onClose: () => void;
  platform: ShareModalPlatforms;
  buttonOptions?: Option[];
}

export const NativeShareModal: React.FunctionComponent<NativeShareModalProps> = ({
  onClose,
  platform,
  buttonOptions,
}) => {
  const { t } = useTranslation('common', { useSuspense: false });
  const ref = createRef<HTMLDivElement>();
  const { shareItems } = useShareContext();

  const [selectedOption, setSelectedOption] = useState<string>(
    buttonOptions ? buttonOptions[0].value : '',
  );

  const { data: basePageData } = useBasePageQuery();

  if (!basePageData?.page) {
    return null;
  }

  const { hero, cover } = basePageData.page;

  let heroImageUrl = (hero?.imageUrl ||
    (cover?.__typename === 'ImageMedia' && cover?.url)) as string;
  let altText =
    hero?.imageTitle || (cover?.__typename === 'ImageMedia' && cover.alt) || '';

  const charity = basePageData?.page?.relationships?.beneficiaries?.nodes
    ? filterByTypename(
        basePageData?.page?.relationships?.beneficiaries?.nodes,
        'Charity',
      )
    : null;
  const charityName = charity?.[0]?.name ?? 'charity';
  const pageUrl = shareItems.instagramShareModal.url;

  const postCopy = `${t('shareModal.instagram.raisingFor', {
    charityName: charityName,
  })}. ${t('shareModal.instagram.supportMeAt', {
    pageUrl: pageUrl,
  })}`;

  const storyCopy = pageUrl;

  return (
    <Modal
      className={styles.modal}
      displayCloseButton
      onCloseRequest={onClose}
      focusLock={false}
    >
      <div className={`row ${styles.topContainer}`}>
        <p className={styles.modalTitle}>
          {platform === ShareModalPlatforms.INSTAGRAM
            ? t('shareModal.instagram.chooseATemplate')
            : null}
          {platform === ShareModalPlatforms.SNAPCHAT
            ? t('shareModal.snapchat.useATemplate')
            : null}
        </p>
      </div>
      {buttonOptions && (
        <JgButtonGroup
          onOptionClick={e => setSelectedOption(e.label)}
          options={buttonOptions}
          selectedOption={selectedOption}
        />
      )}
      <NativeShare
        heroImageUrl={heroImageUrl}
        altText={altText}
        copyText={selectedOption === 'Instagram Post' ? postCopy : storyCopy}
        type={
          selectedOption === ''
            ? SHARE_TYPE.SNAP_STORY
            : selectedOption === 'Instagram Post'
            ? SHARE_TYPE.IG_POST
            : SHARE_TYPE.IG_STORY
        }
      />
      <NativeShareScreenshot
        heroImageUrl={heroImageUrl}
        altText={altText}
        screenshotRef={ref}
        type={
          selectedOption === ''
            ? SHARE_TYPE.SNAP_STORY
            : selectedOption === 'Instagram Post'
            ? SHARE_TYPE.IG_POST
            : SHARE_TYPE.IG_STORY
        }
      />
      <NativeShareButton platform={platform} screenshotRef={ref} />
    </Modal>
  );
};
